import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HandHeart, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";

const MentorshipSection = () => {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ role: "mentor", focus: "", note: "" });

  const load = () => api.get("/mentorship").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.focus.trim()) { toast.info("Add a focus area."); return; }
    try {
      await api.post("/mentorship", form);
      setForm({ role: "mentor", focus: "", note: "" });
      setShowForm(false);
      await load();
      toast.success("You're on the board. Connection is healing.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const mentors = list.filter((m) => m.role === "mentor");
  const mentees = list.filter((m) => m.role === "mentee");

  return (
    <div className="space-y-6" data-testid="mentorship-section">
      <div className="bg-brand text-linen rounded-3xl p-6 sm:p-8">
        <h3 className="font-serif text-3xl">Wellbriety mentorship & tutorship</h3>
        <p className="text-linen/85 mt-2 leading-relaxed max-w-2xl">Walking beside someone — as a mentor or a mentee — is at the heart of the Wellbriety "Red Road." Offer your experience, or ask someone to walk with you. Explore the Wellbriety curriculum and mentorship circles below.</p>
        <a href="https://wellbriety.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm bg-linen text-brand rounded-full px-5 py-2.5 mt-4 font-medium" data-testid="wellbriety-link">Wellbriety curriculum <ExternalLink className="w-3.5 h-3.5" /></a>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} variant="outline" className="rounded-full border-brand text-brand" data-testid="mentorship-toggle"><Plus className="w-4 h-4 mr-1" /> Join the circle</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-3xl border border-linen-3 p-6 space-y-3" data-testid="mentorship-form">
          <div className="flex gap-2">
            {["mentor", "mentee"].map((r) => (
              <button key={r} onClick={() => setForm({ ...form, role: r })} className={`flex-1 rounded-2xl py-3 text-sm capitalize border transition-colors ${form.role === r ? "bg-brand text-linen border-brand" : "bg-linen border-linen-3 text-ink-2"}`} data-testid={`mentorship-role-${r}`}>
                {r === "mentor" ? "I want to mentor" : "I'm seeking a mentor"}
              </button>
            ))}
          </div>
          <Input value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} placeholder="Focus (e.g. sobriety, Wellbriety, trauma, faith)" className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="mentorship-focus" />
          <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="A short note about yourself…" className="rounded-2xl bg-linen border-linen-3 min-h-[80px]" data-testid="mentorship-note" />
          <Button onClick={submit} className="rounded-full bg-brand hover:bg-brand-hover text-linen" data-testid="mentorship-submit">Add me to the circle</Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        {[["Mentors offering support", mentors], ["Seeking a mentor", mentees]].map(([title, group]) => (
          <div key={title}>
            <h4 className="font-serif text-2xl text-brand mb-3">{title}</h4>
            <div className="space-y-3">
              {group.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl border border-linen-3 p-5" data-testid="mentorship-item">
                  <div className="flex items-center gap-2"><HandHeart className="w-4 h-4 text-accent" /><p className="font-medium text-ink">{m.name}</p></div>
                  <p className="text-sm text-brand mt-0.5">{m.focus}</p>
                  {m.note && <p className="text-sm text-ink-2 mt-1 leading-relaxed">{m.note}</p>}
                </div>
              ))}
              {group.length === 0 && <p className="text-sm text-ink-mute">No one here yet — be the first.</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MentorshipSection;
